/**
 * /superadmin/plans — the plan-feature editor.
 *
 * Pick a plan (by code) → a catalog checklist of features → PUT
 * /api/v1/admin/plans/{code}/features. This edits the GLOBAL plan
 * catalogue, so the change affects EVERY tenant on that plan — the
 * confirm step (AlertDialog) spells that out before the write.
 *
 * Plan list source: the PUBLIC GET /api/v1/plans catalogue
 * (billingApi.listPlans) — the same data the pricing page uses. It
 * returns each plan's code/name/current features, which seeds the
 * editor. The catalog allow-list (GET /admin/features/catalog) drives
 * the checkbox set; the backend re-validates every key (422 on a typo).
 */

import { useEffect, useMemo, useState } from "react";
import { createFileRoute } from "@tanstack/react-router";
import { useMutation, useQuery } from "@tanstack/react-query";
import { AlertCircle, Check, Loader2, RotateCcw } from "lucide-react";
import { toast } from "sonner";
import {
  adminConsoleApi,
  billingApi,
  toApiError,
} from "@/lib/api";
import type { PlanDTO } from "@/types/billing";
import { PageHeader } from "@/components/shared/page-header";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { Button } from "@/components/ui/button";
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Checkbox } from "@/components/ui/checkbox";
import { Label } from "@/components/ui/label";
import { Skeleton } from "@/components/ui/skeleton";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "@/components/ui/alert-dialog";

export const Route = createFileRoute("/_authgated/superadmin/plans")({
  component: PlanEditorRoute,
});

function PlanEditorRoute() {
  const plansQuery = useQuery({
    queryKey: ["billing-plans"],
    queryFn: ({ signal }) => billingApi.listPlans(signal),
  });
  const catalogQuery = useQuery({
    queryKey: ["admin-feature-catalog"],
    queryFn: ({ signal }) => adminConsoleApi.featureCatalog(signal),
    staleTime: 5 * 60 * 1000,
  });

  const plans = plansQuery.data?.plans ?? [];
  const catalog = catalogQuery.data?.items ?? [];

  const [selectedCode, setSelectedCode] = useState<string>("");

  // Default to the first plan once loaded.
  useEffect(() => {
    if (!selectedCode && plans.length > 0) {
      setSelectedCode(plans[0].code);
    }
  }, [plans, selectedCode]);

  const selectedPlan = plans.find((p) => p.code === selectedCode);

  return (
    <>
      <PageHeader
        title="Plans"
        description="Edit a plan's feature bundle. Changes apply to every tenant on the plan."
      />

      {plansQuery.error ? (
        <Alert variant="destructive" className="max-w-md">
          <AlertCircle className="h-4 w-4" />
          <AlertTitle>Couldn&apos;t load plans</AlertTitle>
          <AlertDescription>
            {toApiError(plansQuery.error).message}
          </AlertDescription>
        </Alert>
      ) : plansQuery.isLoading ? (
        <Skeleton className="h-64 w-full max-w-2xl" />
      ) : plans.length === 0 ? (
        <Alert className="max-w-md">
          <AlertCircle className="h-4 w-4" />
          <AlertTitle>No plans</AlertTitle>
          <AlertDescription>
            The plan catalogue is empty.
          </AlertDescription>
        </Alert>
      ) : (
        <Card className="max-w-2xl">
          <CardHeader>
            <CardTitle className="text-base">Edit Plan Features</CardTitle>
          </CardHeader>
          <CardContent className="space-y-5">
            <div className="space-y-1.5">
              <Label htmlFor="plan-pick">Plan</Label>
              <Select
                value={selectedCode}
                onValueChange={setSelectedCode}
              >
                <SelectTrigger id="plan-pick" className="max-w-sm">
                  <SelectValue placeholder="Choose a plan" />
                </SelectTrigger>
                <SelectContent>
                  {plans.map((p) => (
                    <SelectItem key={p.code} value={p.code}>
                      {p.name} ({p.code})
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {selectedPlan && (
              <PlanFeatureForm
                key={selectedPlan.code}
                plan={selectedPlan}
                catalog={catalog}
                catalogLoading={catalogQuery.isLoading}
                onSaved={() => {
                  // Refetch the public catalogue so the picker reflects
                  // the new feature set on the next render.
                  void plansQuery.refetch();
                }}
              />
            )}
          </CardContent>
        </Card>
      )}
    </>
  );
}

function PlanFeatureForm({
  plan,
  catalog,
  catalogLoading,
  onSaved,
}: {
  plan: PlanDTO;
  catalog: { key: string; label: string }[];
  catalogLoading: boolean;
  onSaved: () => void;
}) {
  const baseline = useMemo(
    () => new Set<string>(plan.features),
    [plan.features],
  );
  const [selected, setSelected] = useState<Set<string>>(
    () => new Set(plan.features),
  );

  const dirty = useMemo(() => {
    if (selected.size !== baseline.size) return true;
    for (const k of selected) if (!baseline.has(k)) return true;
    return false;
  }, [selected, baseline]);

  const mutation = useMutation({
    mutationFn: (next: string[]) =>
      adminConsoleApi.updatePlanFeatures(plan.code, next),
    onSuccess: () => {
      toast.success(`Updated ${plan.name} features`);
      onSaved();
    },
    onError: (err) => {
      toast.error(toApiError(err).message);
    },
  });

  function toggle(key: string, on: boolean) {
    setSelected((prev) => {
      const next = new Set(prev);
      if (on) next.add(key);
      else next.delete(key);
      return next;
    });
  }

  function reset() {
    setSelected(new Set(plan.features));
  }

  // A plan may carry a feature key no longer in the catalog (legacy).
  // Surface those so an edit doesn't silently drop them — they render
  // as checked, disabled rows below the catalog.
  const catalogKeys = new Set(catalog.map((c) => c.key));
  const orphanFeatures = Array.from(selected).filter(
    (k) => !catalogKeys.has(k),
  );

  return (
    <div className="space-y-4">
      <div>
        <p className="text-sm font-medium">Features</p>
        <p className="text-sm text-muted-foreground">
          Tick the modules this plan grants.
        </p>
      </div>

      {catalogLoading ? (
        <div className="space-y-2">
          <Skeleton className="h-9 w-full" />
          <Skeleton className="h-9 w-full" />
          <Skeleton className="h-9 w-full" />
        </div>
      ) : (
        <div className="grid gap-2 sm:grid-cols-2">
          {catalog.map((entry) => {
            const checked = selected.has(entry.key);
            const inputId = `plan-${plan.code}-${entry.key}`;
            return (
              <Label
                key={entry.key}
                htmlFor={inputId}
                className="flex cursor-pointer items-center gap-3 rounded-md border border-border px-3 py-2.5 hover:bg-accent/50"
              >
                <Checkbox
                  id={inputId}
                  checked={checked}
                  onCheckedChange={(v) => toggle(entry.key, v === true)}
                  disabled={mutation.isPending}
                />
                <span className="min-w-0">
                  <span className="block text-sm font-medium">
                    {entry.label}
                  </span>
                  <span className="block font-mono text-xs text-muted-foreground">
                    {entry.key}
                  </span>
                </span>
              </Label>
            );
          })}
        </div>
      )}

      {orphanFeatures.length > 0 && (
        <Alert>
          <AlertCircle className="h-4 w-4" />
          <AlertTitle>Unknown feature keys on this plan</AlertTitle>
          <AlertDescription>
            {orphanFeatures.join(", ")} — not in the current catalog.
            Saving will drop these (the backend rejects unknown keys), so
            re-add them to the catalog first if they should stay.
          </AlertDescription>
        </Alert>
      )}

      <div className="flex items-center gap-2">
        <AlertDialog>
          <AlertDialogTrigger asChild>
            <Button disabled={!dirty || mutation.isPending}>
              {mutation.isPending ? (
                <Loader2 className="h-4 w-4 animate-spin" />
              ) : (
                <Check className="h-4 w-4" />
              )}
              Save Features
            </Button>
          </AlertDialogTrigger>
          <AlertDialogContent>
            <AlertDialogHeader>
              <AlertDialogTitle>
                Update {plan.name} for every tenant on this plan?
              </AlertDialogTitle>
              <AlertDialogDescription>
                This edits the global plan catalogue. Every tenant whose
                subscription is on <strong>{plan.code}</strong> will gain
                or lose the modules you changed, immediately. This cannot
                be undone with a single click — you&apos;d have to edit
                the plan again.
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel>Cancel</AlertDialogCancel>
              <AlertDialogAction
                onClick={() =>
                  mutation.mutate(Array.from(selected))
                }
              >
                Update Plan
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>

        <Button
          variant="outline"
          onClick={reset}
          disabled={!dirty || mutation.isPending}
        >
          <RotateCcw className="h-4 w-4" />
          Reset
        </Button>
      </div>
    </div>
  );
}
